/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Modal } from 'antd-mobile'
import { Button, CloseBtn } from '@styles/button.style'
import { observer } from 'mobx-react'
import _ from 'lodash'
import { Content, ModalBg } from '@styles/base.style'
import useRootStore from '@store/useRootStore'
import { isMobile } from 'react-device-detect'
import { Theme } from '@themes/theme'
import { GeneralPopupTit } from './index.style'

export interface GeneralPopupOptions {
  wrapClassName?: string
  className?: string
  isScroll?: boolean
  custom?: boolean
  clsBtn?: boolean /* full modal 하단 플로팅 닫기 버튼*/
  closeCallback?: (closeType?: PopupCloseType) => void
  contentClassName?: string
  isUnableCloseBg?: boolean
  isAndroidBackClosable?: boolean
  isAnimated?: boolean
  enableBackHistory?: boolean /* 뒤로가기로 팝업 닫기 */
  isClosable?: () => boolean
}

export interface GeneralPopupProps {
  contentBody: React.ReactElement
  title: string
  options: GeneralPopupOptions
  titleCustom: React.ReactNode
  popupId: string
}

export interface PopupCloseType {
  isTitleClose?: boolean
  isFloatingClose?: boolean
  isModalMaskClose?: boolean
}

const GeneralPopup: React.FC<GeneralPopupProps> = (props: GeneralPopupProps) => {
  const { globalStore } = useRootStore()

  const {
    contentBody,
    title,
    titleCustom,
    options,
    popupId,
  } = props

  const { isUnableCloseBg, isAnimated = false, enableBackHistory = false } = options

  // enableBackHistory인 팝업 중 가장 먼저 열린 것만 히스토리 관리 주체가 됨
  const isHistoryOwner = enableBackHistory &&
    globalStore.generalPopupQueue.find(popupProps => popupProps.options?.enableBackHistory)?.popupId === popupId
  const shouldManageHistory = isHistoryOwner && isMobile
  const totalPopupCount = shouldManageHistory ? globalStore.generalPopupQueue.length : 0
  const prevPopupCountRef = useRef(0) // 직전 팝업 수
  const pushStateCountRef = useRef(0) // 추가한 pushState 항목 수 추적

  const [visible, setVisible] = useState(false)

  // 기본 팝업은 닫을 수 있고, 옵션이 있을 때만 현재 닫힘 상태를 덮어쓴다.
  let isClosable = true
  if (options?.isClosable) {
    isClosable = options.isClosable()
  }

  const onCloseHandler = (closeType?: PopupCloseType, isAndroidBackClose = false) => {
    if (!isClosable) {
      return
    }

    const isButtonClose = closeType?.isTitleClose || closeType?.isFloatingClose || false

    // 배경 닫기는 막되, 팝업에서 명시적으로 허용한 Android Back은 닫기를 허용한다.
    if (isUnableCloseBg && !isButtonClose && !isAndroidBackClose) {
      return
    }
    setVisible(false)
    const { closeCallback } = options
    globalStore.closeGeneralPopup(popupId, isAnimated)
    if (closeCallback && _.isFunction(closeCallback)) {
      closeCallback(closeType)
    }
  }

  const onPopStateHandler = () => {
    pushStateCountRef.current = pushStateCountRef.current - 1
    const topPopup = globalStore.currentGeneralPopup
    if (topPopup && topPopup.popupId !== popupId) {
      globalStore.closeGeneralPopupByBackButton(topPopup.popupId)
      return
    }
    onCloseHandler()
    window.removeEventListener('popstate', onPopStateHandler)
  }

  let customTit
  let closeBtn
  let modalWrapClassName = ''
  let modalClassName = 'full-modal message'
  let contentClass = 'full-modal'
  let contentCss = (theme: Theme) => css`height: 100%;
    overflow: auto;
    text-align: left;`
  if (options) {
    const { wrapClassName: wrapClsName, className: clsName, isScroll, custom, clsBtn, contentClassName } = options
    if (wrapClsName) {
      modalWrapClassName += `${wrapClsName}`
    }
    if (clsName) {
      modalClassName += ` ${clsName}`
    }
    if (contentClassName) {
      contentClass = contentClassName
    }
    if (!isScroll) {
      contentCss = (theme: Theme) => css`height: 100%;
        overflow: hidden;
        text-align: left;`
    }
    if (custom) {
      customTit = (
        <Fragment>
          {titleCustom}
        </Fragment>
      )
    }
    if (clsBtn) {
      if (isMobile) {
        closeBtn = (
          <Button
            TP
            float
            onClick={() => onCloseHandler({ isFloatingClose: true })}
            className="full-modal-cls-btn"
          >
            <div className="btn-bg">
              <span className="cls-ico"/>
            </div>
          </Button>
        )
      }
    }
  }
 useEffect(() => {
   setVisible(true)
 }, [popupId])

  useEffect(() => {
    if (globalStore.backButtonClosePopupId !== popupId) {
      return
    }
    globalStore.clearBackButtonClosePopupId()
    onCloseHandler(undefined, options?.isAndroidBackClosable === true)
  }, [globalStore.backButtonClosePopupId])

  useEffect(() => {
    if (!shouldManageHistory) {
      return
    }

    if (totalPopupCount > prevPopupCountRef.current) {
      window.history.pushState({ generalPopupBackHistory: true }, '', window.location.href)
      pushStateCountRef.current = pushStateCountRef.current + 1
    }
    prevPopupCountRef.current = totalPopupCount
  }, [totalPopupCount])

  useEffect(() => {
    if (!shouldManageHistory) {
      return
    }

    window.addEventListener('popstate', onPopStateHandler)
    return () => {
      window.removeEventListener('popstate', onPopStateHandler)
      if (pushStateCountRef.current > 0) {
        window.history.go(-pushStateCountRef.current) // 남은 pushState 항목 일괄 소비
      }
    }
  }, [])

  return (
    <Modal
      key={popupId}
      popup
      visible={visible}
      animated={isAnimated}
      animationType="slide-up"
      title={
        <GeneralPopupTit>
          {customTit}
          <h1 className="title">{title}</h1>
          {isClosable && (
            <Button
              onClick={() => onCloseHandler({ isTitleClose: true })}
              className="close-btn"
              css={CloseBtn}
              aria-label="닫기"
            >
            </Button>
          )}
        </GeneralPopupTit>
      }
      wrapClassName={modalWrapClassName}
      className={modalClassName}
      onClose={onCloseHandler}
      maskClosable={false}
    >
      <Content css={contentCss} className={contentClass}>
        {contentBody}
      </Content>

      {closeBtn}

      {/* 빠른 적용을 위해 임시로 적용 */}
      {!isMobile && <ModalBg onClick={() => onCloseHandler({ isModalMaskClose: true })}/>}
    </Modal>
  )
}
export default observer(GeneralPopup)
